import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { getUserTimezone, getLocalDateString, formatDateForUserDisplay } from '@/utils/timezone-helpers';
import { getTimezoneDisplayWithOffset } from '@/utils/timezone-detection';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { 
  Loader2, 
  Clock, 
  CalendarClock, 
  Save, 
  Plus, 
  Trash2, 
  Info 
} from 'lucide-react';

interface AvailabilitySettingsProps {
  userId: string;
}

// Default schedule for weekdays
const defaultWeekdaySchedule = {
  enabled: false,
  slots: [{ start: '09:00', end: '17:00' }]
};

// Days of the week
const daysOfWeek = [
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
  { value: 'saturday', label: 'Saturday' },
  { value: 'sunday', label: 'Sunday' },
];

// Time slots for select options (30 min intervals)
const generateTimeOptions = () => {
  const options = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const hourStr = hour.toString().padStart(2, '0');
      const minuteStr = minute.toString().padStart(2, '0');
      const time = `${hourStr}:${minuteStr}`;
      options.push({ value: time, label: time });
    }
  }
  return options;
};

const timeOptions = generateTimeOptions();

const AvailabilitySettings: React.FC<AvailabilitySettingsProps> = ({ userId }) => {
  const { t } = useTranslation('dashboard');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [availabilityRules, setAvailabilityRules] = useState<any>({
    weekly_schedule: {
      monday: { ...defaultWeekdaySchedule },
      tuesday: { ...defaultWeekdaySchedule },
      wednesday: { ...defaultWeekdaySchedule },
      thursday: { ...defaultWeekdaySchedule },
      friday: { ...defaultWeekdaySchedule },
      saturday: { ...defaultWeekdaySchedule },
      sunday: { ...defaultWeekdaySchedule },
    },
    buffer_minutes: 15,
    max_advance_booking_days: 60,
    blackout_dates: [],
    custom_dates: [],
  });
  
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [activeTab, setActiveTab] = useState('weekly');
  const [userTimezone, setUserTimezone] = useState<string>('UTC');
  
  const { toast } = useToast();
  
  // Fetch availability rules and user timezone
  useEffect(() => {
    const fetchAvailabilityRules = async () => {
      if (!userId) {
        setLoading(false);
        return;
      }
      
      try {
        console.log("Fetching availability rules for user ID:", userId);
        
        // Get user timezone
        const timezone = await getUserTimezone(userId);
        setUserTimezone(timezone);
        
        const { data, error } = await supabase
          .from('practitioners')
          .select('availability_rules')
          .eq('id', userId)
          .single();
          
        if (error) {
          console.error("Error fetching availability rules:", error);
          throw error;
        }
        
        if (data?.availability_rules) {
          console.log("Fetched availability rules:", data.availability_rules);
          // Initialize with existing data or defaults for any missing properties
          setAvailabilityRules(prevRules => {
            const fetchedRules = data.availability_rules as any;
            
            // Ensure weekly_schedule has all days with proper structure
            const weekly_schedule = { ...prevRules.weekly_schedule };
            if (fetchedRules.weekly_schedule) {
              daysOfWeek.forEach(day => {
                const dayValue = day.value as keyof typeof weekly_schedule;
                if (fetchedRules.weekly_schedule[dayValue]) {
                  weekly_schedule[dayValue] = {
                    enabled: fetchedRules.weekly_schedule[dayValue].enabled ?? false,
                    slots: fetchedRules.weekly_schedule[dayValue].slots?.length > 0 
                      ? fetchedRules.weekly_schedule[dayValue].slots 
                      : [{ start: '09:00', end: '17:00' }]
                  };
                }
              });
            }
            
            return {
              weekly_schedule,
              buffer_minutes: fetchedRules.buffer_minutes ?? prevRules.buffer_minutes,
              max_advance_booking_days: fetchedRules.max_advance_booking_days ?? prevRules.max_advance_booking_days,
              blackout_dates: fetchedRules.blackout_dates ?? prevRules.blackout_dates,
              custom_dates: fetchedRules.custom_dates ?? prevRules.custom_dates,
            };
          });
        }
      } catch (error: any) {
        console.error('Error fetching availability rules:', error);
        toast({
          variant: 'destructive',
          title: 'Error loading availability',
          description: error.message || 'Could not load your availability settings',
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchAvailabilityRules();
  }, [userId, toast]);
  
  // Toggle day enabled/disabled
  const toggleDayEnabled = (day: string) => {
    console.log(`Toggling day: ${day}, current status:`, availabilityRules.weekly_schedule[day].enabled);
    setAvailabilityRules(prev => {
      const updatedSchedule = { ...prev.weekly_schedule };
      updatedSchedule[day] = {
        ...updatedSchedule[day],
        enabled: !updatedSchedule[day].enabled,
      };
      
      return {
        ...prev,
        weekly_schedule: updatedSchedule,
      };
    });
  };
  
  // Add time slot to a day
  const addTimeSlot = (day: string) => {
    setAvailabilityRules(prev => {
      const updatedSchedule = { ...prev.weekly_schedule };
      updatedSchedule[day] = {
        ...updatedSchedule[day],
        slots: [
          ...updatedSchedule[day].slots,
          { start: '09:00', end: '17:00' }
        ],
      };
      
      return {
        ...prev,
        weekly_schedule: updatedSchedule,
      };
    });
  };
  
  // Remove time slot from a day
  const removeTimeSlot = (day: string, index: number) => {
    setAvailabilityRules(prev => {
      const updatedSchedule = { ...prev.weekly_schedule };
      const updatedSlots = [...updatedSchedule[day].slots];
      updatedSlots.splice(index, 1);
      
      updatedSchedule[day] = {
        ...updatedSchedule[day],
        slots: updatedSlots,
      };
      
      return {
        ...prev,
        weekly_schedule: updatedSchedule,
      };
    });
  };
  
  // Update time slot
  const updateTimeSlot = (day: string, index: number, field: 'start' | 'end', value: string) => {
    setAvailabilityRules(prev => {
      const updatedSchedule = { ...prev.weekly_schedule };
      const updatedSlots = [...updatedSchedule[day].slots];
      updatedSlots[index] = { 
        ...updatedSlots[index], 
        [field]: value 
      };
      
      updatedSchedule[day] = {
        ...updatedSchedule[day],
        slots: updatedSlots,
      };
      
      return {
        ...prev,
        weekly_schedule: updatedSchedule,
      };
    });
  };
  
  // Update buffer minutes
  const updateBufferMinutes = (value: string) => {
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && numValue >= 0) {
      setAvailabilityRules(prev => ({
        ...prev,
        buffer_minutes: numValue,
      }));
    }
  };
  
  // Update max advance booking days
  const updateMaxAdvanceBookingDays = (value: string) => {
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && numValue >= 1) {
      setAvailabilityRules(prev => ({
        ...prev,
        max_advance_booking_days: numValue,
      }));
    }
  };
  
  // Add blackout date
  const addBlackoutDate = () => {
    if (!selectedDate) return;
    
    // Use local date string to avoid timezone conversion issues
    const dateStr = getLocalDateString(selectedDate);
    
    // Check if date already exists
    if (availabilityRules.blackout_dates.includes(dateStr)) {
      toast({
        variant: 'default',
        title: 'Date already added',
        description: 'This date is already in your blackout dates',
      });
      return;
    }
    
    setAvailabilityRules(prev => ({
      ...prev,
      blackout_dates: [...prev.blackout_dates, dateStr],
    }));
    
    setSelectedDate(undefined);
  };
  
  // Remove blackout date
  const removeBlackoutDate = (date: string) => {
    setAvailabilityRules(prev => ({
      ...prev,
      blackout_dates: prev.blackout_dates.filter((d: string) => d !== date),
    }));
  };
  
  // Save availability rules
  const saveAvailabilityRules = async () => {
    if (!userId) {
      toast({
        variant: 'destructive',
        title: 'Error saving',
        description: 'User ID is missing. Please try again or reload the page.',
      });
      return;
    }
    
    setSaving(true);
    
    try {
      console.log("Saving availability rules for user ID:", userId);
      console.log("Data to be saved:", availabilityRules);
      
      const { error } = await supabase
        .from('practitioners')
        .update({
          availability_rules: availabilityRules,
          updated_at: new Date().toISOString(),
        })
        .eq('id', userId);
        
      if (error) {
        console.error("Error saving availability rules:", error);
        throw error;
      }
      
      toast({
        title: 'Availability updated',
        description: 'Your availability settings have been saved',
      });
    } catch (error: any) {
      console.error('Error saving availability rules:', error);
      toast({
        variant: 'destructive',
        title: 'Update failed',
        description: error.message || 'Could not save your availability settings',
      });
    } finally {
      setSaving(false);
    }
  };
  
  // Validate if the time range is valid (start is before end)
  const isValidTimeRange = (start: string, end: string) => {
    return start < end;
  };
  
  // Render function for blackout dates
  const renderBlackoutDates = () => {
    if (!availabilityRules.blackout_dates || availabilityRules.blackout_dates.length === 0) {
      return (
        <div className="text-center py-4 text-muted-foreground">
          No blackout dates set
        </div>
      );
    }
    
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 mt-4">
        {availabilityRules.blackout_dates.sort().map((date: string) => (
          <div
            key={date}
            className="flex items-center justify-between bg-muted p-2 rounded-md"
          >
            <span>{formatDateForUserDisplay(new Date(date + 'T00:00:00'), userTimezone, 'en')}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => removeBlackoutDate(date)}
              className="h-8 w-8 p-0"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>
    );
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2 text-lg">{t('loading')}</span>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Current Timezone Display */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium">{t('availabilitySettings.currentTimezone')}</h3>
              <p className="text-sm text-muted-foreground">{t('availabilitySettings.timezoneNote')}</p>
            </div>
            <Badge variant="outline" className="text-sm">
              {getTimezoneDisplayWithOffset(userTimezone)}
            </Badge>
          </div>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 h-auto">
          <TabsTrigger value="weekly" className="text-xs sm:text-sm lg:text-base px-1 sm:px-2 lg:px-4 py-2 leading-tight">
            <span className="block sm:hidden">{t('availabilitySettings.weekly')}</span>
            <span className="hidden sm:block">{t('availabilitySettings.weeklySchedule')}</span>
          </TabsTrigger>
          <TabsTrigger value="exceptions" className="text-xs sm:text-sm lg:text-base px-1 sm:px-2 lg:px-4 py-2 leading-tight">
            <span className="block sm:hidden">{t('availabilitySettings.exceptions')}</span>
            <span className="hidden sm:block">{t('availabilitySettings.exceptionsBlackout')}</span>
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="weekly" className="space-y-6">
          {/* Buffer Settings */}
          <Card>
            <CardHeader>
              <CardTitle>{t('availabilitySettings.bufferBookingSettings')}</CardTitle>
              <CardDescription>
                {t('availabilitySettings.bufferDescription')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                <div className="space-y-2">
                  <Label htmlFor="buffer-minutes">{t('availabilitySettings.bufferTime')}</Label>
                  <Input
                    id="buffer-minutes"
                    type="number"
                    min="0"
                    value={availabilityRules.buffer_minutes}
                    onChange={(e) => updateBufferMinutes(e.target.value)}
                    className="w-full"
                  />
                  <p className="text-sm text-muted-foreground">
                    {t('availabilitySettings.minimumTimeBetween')}
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="max-days">{t('availabilitySettings.maxAdvanceBooking')}</Label>
                  <Input
                    id="max-days"
                    type="number"
                    min="1"
                    value={availabilityRules.max_advance_booking_days}
                    onChange={(e) => updateMaxAdvanceBookingDays(e.target.value)}
                    className="w-full"
                  />
                  <p className="text-sm text-muted-foreground">
                    {t('availabilitySettings.howFarAdvance')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Weekly Schedule */}
          <Card>
            <CardHeader>
              <CardTitle>{t('availabilitySettings.availableWorkHours')}</CardTitle>
              <CardDescription>
                {t('availabilitySettings.availableWorkHoursDescription')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Help Text */}
              <div className="bg-muted/50 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <Info className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">
                      {t('availabilitySettings.helpText')}
                    </p>
                  </div>
                </div>
              </div>
              
              {daysOfWeek.map((day) => {
                const dayData = availabilityRules.weekly_schedule[day.value];
                
                return (
                  <div key={day.value} className="border rounded-lg p-4">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4 mb-4">
                      <div className="flex items-center gap-4">
                        <Switch
                          id={`day-toggle-${day.value}`}
                          checked={dayData.enabled}
                          onCheckedChange={() => toggleDayEnabled(day.value)}
                        />
                        <Label 
                          htmlFor={`day-toggle-${day.value}`}
                          className="text-base sm:text-lg font-medium cursor-pointer"
                        >
                          {t(`availabilitySettings.daysOfWeek.${day.value}`)}
                        </Label>
                      </div>
                      
                      {dayData.enabled && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => addTimeSlot(day.value)}
                          disabled={saving}
                          className="w-full sm:w-auto flex-shrink-0"
                        >
                          <Plus className="h-4 w-4 sm:mr-1" />
                          <span className="hidden xs:inline sm:inline ml-1">{t('availabilitySettings.addTimeSlot')}</span>
                          <span className="xs:hidden">Add</span>
                        </Button>
                      )}
                    </div>
                    
                    {dayData.enabled ? (
                      <div className="space-y-4">
                        {dayData.slots.map((slot: any, index: number) => (
                          <div key={index} className="space-y-3 lg:space-y-0 lg:grid lg:grid-cols-5 lg:gap-4 lg:items-center border-l-2 border-muted pl-3 lg:pl-0 lg:border-l-0 bg-muted/30 p-3 rounded-md lg:bg-transparent lg:p-0">
                            <div className="lg:col-span-2">
                              <Label htmlFor={`${day.value}-start-${index}`} className="text-sm font-medium">
                                {t('availabilitySettings.startTime')}
                              </Label>
                              <Select
                                value={slot.start}
                                onValueChange={(value) => updateTimeSlot(day.value, index, 'start', value)}
                                disabled={saving}
                              >
                                 <SelectTrigger id={`${day.value}-start-${index}`} className="w-full mt-1">
                                   <SelectValue placeholder={t('availabilitySettings.selectTime')} />
                                 </SelectTrigger>
                                 <SelectContent>
                                   {timeOptions.map(time => (
                                     <SelectItem 
                                       key={time.value} 
                                       value={time.value}
                                     >
                                       {time.label}
                                     </SelectItem>
                                   ))}
                                 </SelectContent>
                              </Select>
                            </div>
                            
                            <div className="lg:col-span-2">
                              <Label htmlFor={`${day.value}-end-${index}`} className="text-sm font-medium">
                                {t('availabilitySettings.endTime')}
                              </Label>
                              <Select
                                value={slot.end}
                                onValueChange={(value) => updateTimeSlot(day.value, index, 'end', value)}
                                disabled={saving}
                              >
                                <SelectTrigger 
                                  id={`${day.value}-end-${index}`}
                                  className={cn("w-full mt-1", {
                                    'border-destructive': !isValidTimeRange(slot.start, slot.end)
                                  })}
                                >
                                  <SelectValue placeholder={t('availabilitySettings.selectTime')} />
                                </SelectTrigger>
                                 <SelectContent>
                                   {timeOptions.map(time => (
                                     <SelectItem 
                                       key={time.value} 
                                       value={time.value}
                                     >
                                       {time.label}
                                     </SelectItem>
                                   ))}
                                 </SelectContent>
                              </Select>
                            </div>
                            
                            <div className="flex justify-start lg:justify-end items-center lg:items-end pt-2 lg:pt-0">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeTimeSlot(day.value, index)}
                                disabled={dayData.slots.length === 1 || saving}
                                className="text-destructive hover:text-destructive w-full lg:w-auto"
                              >
                                <Trash2 className="h-4 w-4 mr-1" />
                                <span>Remove</span>
                              </Button>
                            </div>
                            
                            {!isValidTimeRange(slot.start, slot.end) && (
                              <p className="lg:col-span-5 text-sm text-destructive mt-1">
                                {t('availabilitySettings.endTimeAfterStart')}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="py-2 text-center text-muted-foreground">
                        {t('availabilitySettings.notAvailableDay')}
                      </div>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="exceptions" className="space-y-6">
          {/* Blackout Dates */}
          <Card>
            <CardHeader>
              <CardTitle>{t('availabilitySettings.blackoutDates')}</CardTitle>
              <CardDescription>
                {t('availabilitySettings.markSpecificDates')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col lg:flex-row gap-4 sm:gap-6">
                <div className="flex-1">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    disabled={(date) => {
                      return date < new Date(new Date().setHours(0, 0, 0, 0));
                    }}
                    className="rounded-md border p-2 sm:p-3 pointer-events-auto w-full mx-auto scale-90 sm:scale-100"
                  />
                </div>
                
                <div className="flex-1 space-y-3 sm:space-y-4">
                  <div className="bg-muted rounded-md p-3 sm:p-4">
                    <div className="flex items-start gap-2">
                      <Info className="h-4 w-4 sm:h-5 sm:w-5 text-primary flex-shrink-0 mt-0.5" />
                      <div className="space-y-2">
                        <h4 className="font-medium text-sm sm:text-base">{t('availabilitySettings.aboutBlackoutDates')}</h4>
                        <p className="text-xs sm:text-sm text-muted-foreground">
                          {t('availabilitySettings.blackoutDatesOverride')}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <Button
                    onClick={addBlackoutDate}
                    disabled={!selectedDate || saving}
                    className="w-full text-sm"
                    size="sm"
                  >
                    <Plus className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
                    {t('availabilitySettings.addSelectedDate')}
                  </Button>
                  
                  <div className="space-y-2">
                    <h4 className="font-medium">{t('availabilitySettings.currentBlackoutDates')}</h4>
                    {renderBlackoutDates()}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={saveAvailabilityRules}
          disabled={saving || !userId}
          size="lg"
        >
          {saving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t('availabilitySettings.saving')}
            </>
          ) : (
            <>
              <Save className="mr-2 h-4 w-4" />
              {t('availabilitySettings.saveSettings')}
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default AvailabilitySettings;
